import { useState, useEffect } from 'react';

/**
 * Returns the pixel offset caused by the iOS virtual keyboard.
 *
 * On iOS Safari, `position: fixed; bottom: 0` is relative to the initial
 * containing block (ICB) which does **not** shrink when the keyboard opens.
 * This hook listens to the `visualViewport` API to compute the gap between
 * the ICB bottom and the top of the keyboard so consumers can shift
 * fixed-position elements (e.g. bottom drawers) upward.
 *
 * Returns `0` on SSR, on browsers without `visualViewport`, and when the
 * keyboard is closed.
 *
 * `enabled` exists because these listeners are not cheap where they are not
 * needed: `scroll` fires on every frame of a scroll, and iOS moves the viewport
 * as the URL bar collapses, so an offset that no one is reading still re-renders
 * its owner continuously. Surfaces that mount closed on every page — the auth,
 * enquiry and download gates all do — should pass their open state.
 */
export function useIOSKeyboardOffset(enabled = true): number {
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        if (!enabled) {
            setOffset(0);
            return;
        }
        const vv = window.visualViewport;
        if (!vv) return;

        const update = () => {
            const keyboardHeight =
                window.innerHeight - vv.height - vv.offsetTop;
            // Rounded because the viewport reports subpixel heights through the
            // whole keyboard animation. Unrounded, every frame is a new value
            // and a new render for something no one can see; rounded, React
            // bails out on the frames that did not actually move it.
            setOffset(Math.round(Math.max(0, keyboardHeight)));
        };

        // Compute initial offset in case the keyboard is already open at mount.
        update();

        // Listen to resize only. iOS fires `scroll` on visualViewport as it
        // tries to keep a focused field in view; applying that as layout
        // moves the field and dismisses the keyboard.
        vv.addEventListener('resize', update);

        return () => {
            vv.removeEventListener('resize', update);
        };
    }, [enabled]);

    return offset;
}
